import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonDirective, SpinnerComponent } from '@coreui/angular-pro';
import { TranslatePipe } from 'i18n';

import {
  CompanionDay, CompanionMetrics, CompanionObservation, CompanionOverview, HealthCompanionService
} from '../../../services/health-companion.service';
import { formatValue, isNumericKind } from '../../../services/companion-format';

const PAGE_DAYS = 14;

/**
 * The "Tagebuch" tab (Gesundheits- & Fitnesstagebuch, plan §7 + §13.3): the
 * computed long-term figures, then one block per day with every value (blood
 * pressure and pulse as a list of readings), the agreed plan and its review.
 * A value is corrected in place — which creates a new row and keeps the old
 * one in the chain (shown collapsed). No charts in stage 1.
 */
@Component({
  selector: 'app-companion-diary',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, ButtonDirective, SpinnerComponent],
  templateUrl: './companion-diary.component.html',
  styles: [`
    .metrics { display: grid; gap: .6rem; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); }
    .metric { background: var(--gh-sage-soft, #f1f5ef); border-radius: 10px; padding: .6rem .8rem; }
    .metric b { display: block; font-size: 1.25rem; line-height: 1.1; }
    .metric span { color: var(--gh-text-muted, #6b6b6b); font-size: .78rem; }
    .day__h { align-items: center; display: flex; flex-wrap: wrap; gap: .5rem; justify-content: space-between; }
    .obs { border-top: 1px solid var(--gh-border, #e9e5dd); display: grid; gap: .4rem; grid-template-columns: 9rem 1fr auto; padding: .35rem 0; }
    .obs:first-of-type { border-top: 0; }
    .obs--old { color: var(--gh-text-muted, #6b6b6b); text-decoration: line-through; }
    .obs--unclear { background: #fff7e6; }
    .edit { align-items: center; display: flex; flex-wrap: wrap; gap: .35rem; }
    .edit input { max-width: 7rem; }
    @media (max-width: 575.98px) { .obs { grid-template-columns: 1fr; } }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanionDiaryComponent {
  private companion  = inject(HealthCompanionService);
  private destroyRef = inject(DestroyRef);

  overview = input.required<CompanionOverview>();
  readOnly = input(false);
  lang     = input('en');
  changed  = output<void>();

  loading = signal(true);
  days    = signal<CompanionDay[]>([]);
  metrics = signal<CompanionMetrics | null>(null);
  from    = signal<string | null>(null);
  showOld = signal<Set<string>>(new Set());
  editing = signal<number | null>(null);
  editValue = signal('');
  editValue2 = signal('');
  editText = signal('');
  error   = signal<string | null>(null);

  readonly formatValue = formatValue;
  readonly isNumeric = isNumericKind;
  weightUnit = computed(() => this.overview().profile?.weight_unit ?? 'kg');

  constructor() { this.load(); }

  load(before?: string): void {
    this.loading.set(true);
    const to = before ? this.shiftDate(before, -1) : undefined;
    const from = to ? this.shiftDate(to, -(PAGE_DAYS - 1)) : undefined;
    this.companion.days(from, to).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: r => {
        this.days.update(list => before ? [...list, ...r.days] : r.days);
        this.metrics.set(r.metrics);
        this.from.set(r.from);
        this.loading.set(false);
      },
      error: () => { this.loading.set(false); this.error.set('error'); }
    });
  }

  earlier(): void { if (this.from()) this.load(this.from()!); }

  private shiftDate(iso: string, days: number): string {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  }

  current(day: CompanionDay): CompanionObservation[] { return day.observations.filter(o => !o.superseded_by_id); }
  old(day: CompanionDay): CompanionObservation[] { return day.observations.filter(o => !!o.superseded_by_id); }

  toggleOld(date: string): void {
    this.showOld.update(s => { const n = new Set(s); n.has(date) ? n.delete(date) : n.add(date); return n; });
  }

  startEdit(o: CompanionObservation): void {
    this.editing.set(o.id);
    this.editValue.set(o.value == null ? '' : String(o.value));
    this.editValue2.set(o.value2 == null ? '' : String(o.value2));
    this.editText.set(o.text ?? '');
  }

  saveEdit(o: CompanionObservation): void {
    const change = isNumericKind(o.kind)
      ? { value: this.editValue() === '' ? null : Number(this.editValue()), value2: this.editValue2() === '' ? null : Number(this.editValue2()) }
      : { text: this.editText() };
    this.companion.correct(o.id, change).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.editing.set(null); this.load(); this.changed.emit(); },
      error: err => this.error.set(err?.error?.error === 'value_invalid' ? 'value_invalid' : 'error')
    });
  }

  markSkipped(o: CompanionObservation): void {
    this.companion.correct(o.id, { state: 'skipped' }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.editing.set(null); this.load(); this.changed.emit(); },
      error: () => this.error.set('error')
    });
  }

  kg(v: number | string | null): string {
    if (v == null) return '—';
    const n = typeof v === 'number' ? v : parseFloat(v);
    const unit = this.weightUnit();
    const shown = unit === 'lb' ? n / 0.45359237 : n;
    return `${shown.toFixed(1)} ${unit}`;
  }

  signed(v: number | string | null): string {
    if (v == null) return '—';
    const n = typeof v === 'number' ? v : parseFloat(v);
    const unit = this.weightUnit();
    const shown = unit === 'lb' ? n / 0.45359237 : n;
    return `${shown > 0 ? '+' : ''}${shown.toFixed(1)} ${unit}`;
  }
}
